import { z } from "zod";

export const EXPORT_STATUSES = ["REQUESTED", "PROCESSING", "READY", "FAILED"] as const;
export type ExportStatusFilter = "" | (typeof EXPORT_STATUSES)[number];

export interface ExportListCriteria {
  readonly status: ExportStatusFilter;
  readonly exportType: "" | "SETTLEMENT";
  readonly requestedFrom: string;
  readonly requestedTo: string;
  readonly page: number;
  readonly size: number;
}

export interface ExportRequestValues {
  readonly from: string;
  readonly to: string;
  readonly reason: string;
}

const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

function validDateOnly(value: string) {
  if (!dateOnly.test(value)) return false;
  const [year, month, day] = value.split("-").map(Number);
  const date = new Date(Date.UTC(year!, month! - 1, day));
  return date.getUTCFullYear() === year && date.getUTCMonth() === month! - 1 && date.getUTCDate() === day;
}

function plusOneYear(value: string) {
  const [year, month, day] = value.split("-").map(Number);
  const lastDay = new Date(Date.UTC(year! + 1, month!, 0)).getUTCDate();
  return `${year! + 1}-${String(month).padStart(2, "0")}-${String(Math.min(day!, lastDay)).padStart(2, "0")}`;
}

export const exportRequestSchema = z.object({
  from: z.string().refine(validDateOnly, "시작일을 입력해 주세요."),
  to: z.string().refine(validDateOnly, "종료일을 입력해 주세요."),
  reason: z.string().trim().min(5, "요청 사유를 5자 이상 입력해 주세요.").max(500, "요청 사유는 500자까지 입력할 수 있습니다."),
}).superRefine((value, context) => {
  if (!validDateOnly(value.from) || !validDateOnly(value.to)) return;
  if (value.from > value.to) {
    context.addIssue({ code: "custom", path: ["to"], message: "종료일은 시작일보다 빠를 수 없습니다." });
  } else if (value.to > plusOneYear(value.from)) {
    context.addIssue({ code: "custom", path: ["to"], message: "정산 기간은 최대 1년까지 선택할 수 있습니다." });
  }
});

export const exportListFilterSchema = z.object({
  requestedFrom: z.string().refine((value) => !value || validDateOnly(value), "요청 시작일을 확인해 주세요."),
  requestedTo: z.string().refine((value) => !value || validDateOnly(value), "요청 종료일을 확인해 주세요."),
}).superRefine((value, context) => {
  if (value.requestedFrom && value.requestedTo && value.requestedFrom > value.requestedTo) {
    context.addIssue({ code: "custom", path: ["requestedTo"], message: "요청 종료일은 시작일보다 빠를 수 없습니다." });
  }
});

export function parseExportStatus(value: string | null): ExportStatusFilter {
  return EXPORT_STATUSES.includes(value as (typeof EXPORT_STATUSES)[number])
    ? value as ExportStatusFilter
    : "";
}

export function parseExportType(value: string | null): "" | "SETTLEMENT" {
  return value === "SETTLEMENT" ? value : "";
}

export function parseExportPage(value: string | null) {
  if (!value || !/^\d+$/.test(value)) return 0;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : 0;
}

export function parseExportDate(value: string | null) {
  return value && validDateOnly(value) ? value : "";
}

export function parseExportJobId(value: string | null) {
  const parsed = value?.trim() ?? "";
  return parsed && parsed.length <= 100 ? parsed : undefined;
}

export function defaultExportRequest(today = new Date()): ExportRequestValues {
  const to = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Asia/Seoul",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(today);
  return { from: `${to.slice(0, 7)}-01`, to, reason: "" };
}
